import React, { useContext, useEffect, useState } from 'react'
import { Box, Button, Collapse, IconButton, MenuItem, Paper, Stack, Tooltip, TextField, Typography } from '@mui/material'
import { MdExpandMore } from "react-icons/md";
import { commaMoneyFormat, formatDate, frequencyOptions } from '../../../Utils';
import { CaseContext } from '../../../context/CaseContext';
import { PaymentPlanContext } from '../../../context/PaymentPlanContext';


const PaymentPlanModal = (props) => {

    const {
        paymentPlanModal,
        setPaymentPlanModal,
        toast,
        updateBalanceRows,
        updateCaseBalance,
        violationRows,
        violationView,
    } = props;

    const { vioTransactionTbl } = useContext(CaseContext);

    const {
        paymentPlanFrequencyList,
        paymentPlanPeriodicityList,
        paymentPlanTypes,
        handlePaymentPlanFieldChange,
        handleResetPaymentPlanState,
        paymentPlanState,
        createPaymentPlan,
        retrievePaymentPlanByCaseId,
        frequencyDetails,
        setFrequencyDetails
    } = useContext(PaymentPlanContext);

    const [collapse,] = useState('COLLAPSE');
    const [expand,] = useState('EXPAND');

    function handleClose() {
        setPaymentPlanModal(!paymentPlanModal);
        handleResetPaymentPlanState();
        return;
    }

    async function handleSavePaymentPlan(paymentPlanState, violationView) {
        try {
            const data = await createPaymentPlan(paymentPlanState, violationView.fkCaseId, violationView.pkViolationId);
            setPaymentPlanModal(!paymentPlanModal);
            await retrievePaymentPlanByCaseId(violationView.fkCaseId);
            await updateCaseBalance();
            await updateBalanceRows();
            await toast.success(`${data.message}`, {
                position: 'top-right',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
            });
        }
        catch (err) {
            console.log('err handle save payment plan', err);
            await toast.error('Error Creating Payment Plan', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
            });
        }
    };

    useEffect(() => {
        if (violationView && violationView?.remainingBalance) {
            handlePaymentPlanFieldChange('planAmount', new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(violationView.remainingBalance));
        }
    }, [violationView]);

    return (
        <>
            <Paper sx={{ width: 'auto', display: 'flex', height: 'auto', flexDirection: 'column', margin: 'auto', padding: 'auto' }}>
                <Paper className="border-radius-bottom" sx={{ textAlign: 'center', backgroundColor: 'steelblue', color: 'white', p: '1vh' }} elevation={10}>
                    <h1>Create Payment Plan</h1>
                </Paper>
                <Paper sx={{ p: '1vh', width: '98%', height: '50%', m: 'auto', mt: '1vh', backgroundColor: '#' }} elevation={3}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: '.5rem', height: '50%' }}>
                        <strong>
                            <p>Statute: {violationView?.currentStatute || violationView?.description}</p>
                            <p>Description: {violationView?.description || ''}</p>
                            <p>Charge Type: {violationView?.chargeType || ''}</p>
                        </strong>
                        {violationView?.amendedDate && (
                            <strong>
                                <p style={{ color: '#E3D026' }}>Violation Amended To: {violationRows?.find(i => vioTransactionTbl.amendedToViolation === i.id)?.currentStatute}</p>
                                <p style={{ color: '#E3D026' }}>Description: {violationRows?.find(i => vioTransactionTbl.amendedToViolation === i.id)?.description || ''}</p>
                                <p style={{ color: '#E3D026' }}>Charge Type: {violationRows?.find(i => vioTransactionTbl.amendedToViolation === i.id)?.chargeType || ''}</p>
                                <p style={{ color: '#E3D026' }}>Amended Date: {formatDate(violationView?.amendedDate?.split("T")[0]) || ''}</p>
                            </strong>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: "2rem", pr: "1rem" }}>
                            <strong style={{ textAlign: "left" }}>
                                <p>Fine Total: </p>
                                <p>Cost Total: </p>
                                <p>Fee Total: </p>
                                <p>Balance Due: </p>
                            </strong>
                            <strong>
                                <p>{commaMoneyFormat(violationView?.totalFines)}</p>
                                <p>{commaMoneyFormat(violationView?.totalCosts)}</p>
                                <p>{commaMoneyFormat(violationView?.totalFees)}</p>
                                <p>{commaMoneyFormat(violationView?.remainingBalance)}</p>
                            </strong>
                        </Box>
                    </Box>
                </Paper>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', p: 2 }}>
                    <Typography variant="h6" sx={{ mb: '1vh' }}>Select Payment Plan Template</Typography>
                    <TextField
                        id="planType"
                        name="planType"
                        label="Payment Plan Template"
                        select
                        fullWidth
                        required
                        slotProps={{
                            inputLabel: { shrink: true }
                        }}
                        value={paymentPlanState?.planType}
                        onChange={({ target }) => handlePaymentPlanFieldChange('planType', target.value)}
                    >
                        <MenuItem
                            value=''
                        >
                            N/A
                        </MenuItem>
                        {
                            paymentPlanTypes?.map((planType) => {
                                return <MenuItem
                                    key={planType.id}
                                    value={planType.id}
                                >
                                    {planType.description}
                                </MenuItem>
                            })
                        }
                    </TextField>
                    <Typography variant="h6" sx={{ mb: '.25vh', mt: '1vh' }}>Installment Conditions</Typography>

                    <Stack direction="row" spacing={2} sx={{ mt: '1rem', mb: '1rem' }}>
                        <TextField
                            id="planAmount"
                            name="planAmount"
                            label="PAYMENT PLAN AMOUNT"
                            placeHolder="Amount To Be Paid For Each Scheduled Payment"
                            value={paymentPlanState?.planAmount}
                            fullWidth
                            required
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                            sx={{ width: '33%' }}
                            onChange={({ target }) => handlePaymentPlanFieldChange('planAmount', target.value.replace(/[^0-9.]/g, ''))}
                            onFocus={({ target }) => handlePaymentPlanFieldChange('planAmount', target.value.replace(/^[^0-9.]/g, ''))}
                            onBlur={({ target }) => {
                                handlePaymentPlanFieldChange('planAmount',
                                    new Intl.NumberFormat('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        maximumFractionDigits: 2,
                                    })
                                        .format(target.value.replace(/[^0-9.]/g, '')))
                            }}
                        />
                        <TextField
                            id="planStartDate"
                            name="planStartDate"
                            label="PLAN START DATE"
                            value={paymentPlanState?.planStartDate}
                            type="date"
                            required
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                            sx={{ width: '33%' }}
                            onChange={({ target }) => handlePaymentPlanFieldChange('planStartDate', target.value)}
                        >
                        </TextField>
                        <TextField
                            id="planEndDate"
                            name="planEndDate"
                            label="PLAN END DATE"
                            required
                            value={paymentPlanState?.planEndDate}
                            type="date"
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                            sx={{ width: '33%' }}
                            onChange={({ target }) => handlePaymentPlanFieldChange('planEndDate', target.value)}
                            error={paymentPlanState?.planEndDate != null && paymentPlanState?.planStartDate >= paymentPlanState?.planEndDate}
                            color={paymentPlanState?.planEndDate != null && paymentPlanState?.planStartDate >= paymentPlanState?.planEndDate && 'error'}
                            helperText={paymentPlanState.planEndDate != null && paymentPlanState?.planStartDate >= paymentPlanState?.planEndDate && 'PLAN END DATE MUST BE AFTER START DATE'}
                        >
                        </TextField>
                    </Stack>
                    {/*<Typography variant="h6" sx={{ mb: '.25vh', mt: '1vh' }}>INITIAL DUE DATE</Typography>*/}
                    {/*<Paper sx={{ backgroundColor: 'rgba(59, 47, 25, 0.3)', border: '1px solid #5a3c06' }}>*/}
                    {/*    <Typography variant='caption'>Payment Plans Will Default The Initial Due Date To Being Set Based On The Frequency Of The Plan. For Example:*/}
                    {/*        <Box sx={{ display: 'flex', p: '1rem' }}><Typography variant="subtitle2">A MONTHLY PAYMENT STARTING TODAY WILL HAVE THE DUE DATE SET TO BE A MONTH FROM TODAY</Typography></Box>*/}
                    {/*    </Typography>*/}
                    {/*</Paper>*/}
                    {/*<Stack direction="row" spacing={2} sx={{ mt: '1rem', mb: '1rem' }}>*/}
                    {/*    <TextField*/}
                    {/*        id="planStartDate"*/}
                    {/*        name="planStartDate"*/}
                    {/*        label="PLAN START DATE"*/}
                    {/*        value={paymentPlanState?.planStartDate}*/}
                    {/*        type="date"*/}
                    {/*        required*/}
                    {/*        slotProps={{*/}
                    {/*            inputLabel: { shrink: true }*/}
                    {/*        }}*/}
                    {/*        sx={{ width: '33%' }}*/}
                    {/*        onChange={({ target }) => handlePaymentPlanFieldChange('planStartDate', target.value)}*/}
                    {/*    >*/}
                    {/*    </TextField>*/}
                    {/*    <TextField*/}
                    {/*        id="planEndDate"*/}
                    {/*        name="planEndDate"*/}
                    {/*        label="PLAN END DATE"*/}
                    {/*        required*/}
                    {/*        value={paymentPlanState?.planEndDate}*/}
                    {/*        type="date"*/}
                    {/*        slotProps={{*/}
                    {/*            inputLabel: { shrink: true }*/}
                    {/*        }}*/}
                    {/*        sx={{ width: '33%' }}*/}
                    {/*        onChange={({ target }) => handlePaymentPlanFieldChange('planEndDate', target.value)}*/}
                    {/*        error={paymentPlanState?.planEndDate != null && paymentPlanState?.planStartDate >= paymentPlanState?.planEndDate}*/}
                    {/*        color={paymentPlanState?.planEndDate != null && paymentPlanState?.planStartDate >= paymentPlanState?.planEndDate && 'error'}*/}
                    {/*        helperText={paymentPlanState.planEndDate != null && paymentPlanState?.planStartDate >= paymentPlanState?.planEndDate && 'PLAN END DATE MUST BE AFTER START DATE'}*/}
                    {/*    >*/}
                    {/*    </TextField>*/}
                    {/*</Stack>*/}
                    <Stack direction="row" spacing={2}>
                        <Typography variant="h6" sx={{ mb: '.25vh', mt: '1vh' }}>Payment Frequency Conditions</Typography>
                        <Tooltip title={`${frequencyDetails ? collapse : expand} to ${frequencyDetails ? 'HIDE' : 'VIEW'} Frequency Conditions`} placement="right">
                            <IconButton
                                onClick={() => { setFrequencyDetails(!frequencyDetails) }}
                                sx={{ transform: !frequencyDetails ? 'rotate(180deg)' : 'rotate(0deg)', ml: 'auto' }}
                            >
                                <MdExpandMore />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Collapse in={frequencyDetails} timeout="auto" unmountOnExit>
                        <Stack direction="row" spacing={2} sx={{ mt: '1rem', mb: '1rem' }}>
                            <TextField
                                id="frequencyId"
                                name="frequencyID"
                                label="Occurrences"
                                required
                                select
                                fullWidth
                                slotProps={{
                                    inputLabel: { shrink: true }
                                }}
                                value={paymentPlanState?.frequencyId}
                                onChange={({ target }) => handlePaymentPlanFieldChange('frequencyId', target.value)}
                            >
                                <MenuItem
                                    value=''
                                >
                                    N/A
                                </MenuItem>
                                {
                                    paymentPlanFrequencyList?.map((frequency) => {
                                        return <MenuItem
                                            key={frequency.id}
                                            value={frequency.id}
                                        >
                                            {frequencyOptions[frequency.frequency]}
                                        </MenuItem>
                                    })
                                }
                            </TextField>
                            <TextField
                                id="periodicityId"
                                name="periodicityId"
                                label="Payment Frequency"
                                select
                                fullWidth
                                required
                                slotProps={{
                                    inputLabel: { shrink: true }
                                }}
                                onChange={({ target }) => { handlePaymentPlanFieldChange('periodicityId', target.value) }}
                                value={paymentPlanState?.periodicityId}
                            >
                                <MenuItem
                                    value=''
                                >
                                    N/A
                                </MenuItem>
                                {
                                    paymentPlanPeriodicityList?.map((periodicity) => {
                                        return <MenuItem
                                            key={periodicity.id}
                                            value={periodicity.id}
                                        >
                                            {periodicity.periodicity}
                                        </MenuItem>
                                    })
                                }
                            </TextField>
                        </Stack>
                    </Collapse>
                </Box>
                <Box display="flex" flexDirection="row" marginLeft="auto" paddingRight="1rem" sx={{ justifyContent: 'space-between', mb: '1vh' }}>
                    <Tooltip title={paymentPlanState.disabled && <h6>ALL FIELDS ARE REQUIRED</h6>}>
                        <span>
                            <Button
                                onClick={async () => { await handleSavePaymentPlan(paymentPlanState, violationView) }}
                                variant="contained"
                                sx={{ backgroundColor: 'steelblue', color: 'white', marginRight: '1rem' }}
                                disabled={paymentPlanState.disabled || paymentPlanState?.planStartDate >= paymentPlanState?.planEndDate}
                            >
                                Create Plan
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{ backgroundColor: 'steelblue', color: 'white' }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </>
    )
}

export default PaymentPlanModal;